<script lang="ts" setup>
import { onMounted, ref } from 'vue';

import {
  useCoreStore,
  useRouterStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';

import { AppSwiperGallery, AppRouterLink } from '@libs/ui';

import type {
  AccommodationProductCardData,
  AccommodationSegmentContext,
} from '../../../models';
import ImgProductCardLayout from '../ImgProductCardLayout.vue';
import ProductCardFooter from '../ProductCardFooter.vue';
import AlternativeSuggestionHeader from '../AlternativeSuggestionHeader.vue';

import AccommodationAlternativeProductCardContent from './AccommodationAlternativeProductCardContent.vue';

interface AccommodationAlternativeProductCardProps {
  productCardData: AccommodationProductCardData;
  context: AccommodationSegmentContext;
}

const props = defineProps<AccommodationAlternativeProductCardProps>();

interface AccommodationProductCardEmits {
  (event: 'confirm-alternative'): void;
  (event: 'show-details'): void;
  (event: 'show-rooms'): void;
  (event: 'navigation-triggered'): void;
}

const emit = defineEmits<AccommodationProductCardEmits>();

const coreStore = useCoreStore();
const statsigEventsStore = useStatsigEventsStore();
const routerStore = useRouterStore();

onMounted(() => {
  statsigEventsStore.logTripOverviewAlternativeRoomsDisplayed();
});

const enableHoverColor = ref<boolean>(false);

function onGalleryImageSwipe(): void {
  statsigEventsStore.logTripOverviewAlternativeAccommodationGalleryImageSwipe();
}
</script>

<template>
  <div class="accommodation-alternative-product-card">
    <ImgProductCardLayout :enable-hover-color="enableHoverColor">
      <template #header>
        <AlternativeSuggestionHeader
          :info-text="
            $t('planner.no_availability.messages.alternative_selected_room')
          "
          @confirm="emit('confirm-alternative')"
        >
          <template #secondary-button>
            <AppRouterLink
              :link="
                routerStore.getHotelSelectionUrl(props.context.ingredientIndex)
              "
              font="font-link-s-light"
              color="color-neutral-70"
              @navigation-triggered="emit('navigation-triggered')"
            >
              {{ $t('planner.see_other_hotels') }}
            </AppRouterLink>
          </template>
        </AlternativeSuggestionHeader>
      </template>

      <template #image>
        <AppSwiperGallery
          :image-urls="props.productCardData.imgSrcs"
          :show-nav-buttons="true"
          :display-gallery-dots="false"
          :allow-full-gallery="false"
          :error-image-handler="coreStore.handleImageLoadError"
          :left-arrow-aria-label="$t('a11y.previous_image')"
          :right-arrow-aria-label="$t('a11y.next_image')"
          :full-gallery-aria-label="$t('a11y.open_image_gallery')"
          class="image"
          @click="emit('show-details')"
          @gallery-image-swipe="onGalleryImageSwipe"
          @mouse-enter="enableHoverColor = true"
          @mouse-leave="enableHoverColor = false"
        />
      </template>

      <template #content>
        <AccommodationAlternativeProductCardContent
          :product-card-content="props.productCardData.productCardContent"
          :context="props.context"
          @show-details="emit('show-details')"
          @show-rooms="emit('show-rooms')"
          @mouse-enter-content="enableHoverColor = true"
          @mouse-leave-content="enableHoverColor = false"
        />
      </template>
      <template #footer>
        <ProductCardFooter
          @mouse-enter="enableHoverColor = true"
          @mouse-leave="enableHoverColor = false"
          @show-details="emit('show-details')"
        />
      </template>
    </ImgProductCardLayout>
  </div>
</template>

<style lang="scss" scoped>
.accommodation-alternative-product-card {
  .image {
    cursor: pointer;
  }
}
</style>
